/**
 * TabBar - bottom navigation for the business and private workspaces.
 * Active state driven by preact-router URL.
 * Chat tab shows a WebSocket connection dot.
 * Approvals tab shows a pending-count badge.
 * Today tab shows an urgent-count dot (fed by the Today store in P3).
 */

import { useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import { route, useRouter } from 'preact-router';
import { wsConnected } from '../core/ws/client';
import { api } from '../core/api/client';
import styles from './TabBar.module.css';

/** Pending approval count - refreshed on mount + ws updates */
export const pendingCount = signal(0);

/**
 * Count of "needs you now" items on Today (deadlines inside 48h, pending
 * approvals, etc). Defaults to 0 (renders nothing - no fake data). The Today
 * store (P3) is responsible for setting this from real aggregated data; P1a
 * only lands the signal + the render wiring for the dot.
 */
export const todayUrgentCount = signal(0);

/** Refresh the pending approval count from the API */
export async function refreshPendingCount() {
  try {
    const res = await api.getApprovals('pending');
    pendingCount.value = res.total;
  } catch {
    // Silently ignore - badge just won't update
  }
}

interface TabDef {
  id: string;
  label: string;
  path: string;
  icon: () => preact.JSX.Element;
}

const tabs: TabDef[] = [
  {
    id: 'today',
    label: 'Today',
    path: '/today',
    // Sun-horizon: half sun resting on a horizon line, a few rays above.
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M5.5 17a6.5 6.5 0 0113 0" />
        <line x1="2.5" y1="17" x2="21.5" y2="17" />
        <line x1="12" y1="6.5" x2="12" y2="8.5" />
        <line x1="6.3" y1="9.3" x2="7.7" y2="10.7" />
        <line x1="17.7" y1="9.3" x2="16.3" y2="10.7" />
      </svg>
    ),
  },
  {
    id: 'deals',
    label: 'Deals',
    path: '/deals',
    // Key: bow + shaft + two teeth.
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <circle cx="8" cy="12" r="3.5" />
        <line x1="11.3" y1="12" x2="21" y2="12" />
        <line x1="17" y1="12" x2="17" y2="15" />
        <line x1="14" y1="12" x2="14" y2="14.5" />
      </svg>
    ),
  },
  {
    id: 'chat',
    label: 'Chat',
    path: '/',
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
  },
  {
    id: 'approvals',
    label: 'Approvals',
    path: '/approvals',
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    id: 'finance',
    label: 'Finance',
    path: '/finance',
    // Wallet with a card slot.
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M4 6.5h14a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2v-11a2 2 0 012-2h12" />
        <path d="M15 11h7v5h-7a2.5 2.5 0 010-5z" />
        <circle cx="16" cy="13.5" r=".75" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    id: 'watches',
    label: 'Watches',
    path: '/watches',
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M6 3h12v18l-6-4-6 4V3z" /><path d="M9 8h6M9 11h4" />
      </svg>
    ),
  },
];

function isTabActive(tabPath: string, currentUrl: string): boolean {
  if (tabPath === '/') {
    return currentUrl === '/' || currentUrl.startsWith('/chat');
  }
  return currentUrl.startsWith(tabPath);
}

export function TabBar() {
  const [routerState] = useRouter();
  const currentUrl = routerState?.url ?? '/';

  // Fetch pending count on mount
  useEffect(() => {
    refreshPendingCount();
  }, []);

  return (
    <nav class={styles.tabBar}>
      {tabs.map((tab) => {
        const active = isTabActive(tab.path, currentUrl);
        return (
          <button
            key={tab.id}
            class={`${styles.tab} ${active ? styles.tabActive : ''}`}
            onClick={() => route(tab.path)}
            aria-label={tab.label}
            aria-current={active ? 'page' : undefined}
          >
            {/* Chat tab gets the status dot wrapper; Today gets the urgent dot */}
            {tab.id === 'chat' ? (
              <span class={styles.iconWrap}>
                {tab.icon()}
                <span
                  class={`${styles.statusDot} ${
                    wsConnected.value ? '' : styles.statusDotDisconnected
                  }`}
                  title={wsConnected.value ? 'Connected' : 'Disconnected'}
                />
              </span>
            ) : tab.id === 'today' && todayUrgentCount.value > 0 ? (
              <span class={styles.iconWrap}>
                {tab.icon()}
                <span class={styles.urgentDot} title="Needs your attention" />
              </span>
            ) : (
              tab.icon()
            )}

            <span class={styles.label}>{tab.label}</span>

            {/* Approvals badge */}
            {tab.id === 'approvals' && pendingCount.value > 0 && (
              <span class={styles.badge}>{pendingCount.value}</span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
